import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './LoginScreen.css';
import Icon from './Icon';
import logo from '../assets/logo.png';
import officeBg from '../assets/login/corporate_office_bg.jpg';
import iconMultiCompany from '../assets/login/multi_company_icon.png';
import iconSecureVoting from '../assets/login/secure_voting_icon.png';
import iconOfficialDocs from '../assets/login/official_documentation_icon.png';
import iconCeo from '../assets/login/ceo_icon.png';
import iconSecretary from '../assets/login/secretary_icon.png';
import iconBoardMember from '../assets/login/board_member_icon.png';
import iconShareholders from '../assets/login/shareholders_icon.png';
import iconSuperAdmin from '../assets/login/super_admin_icon.png';
import { loginRequest, registerRequest, resendOtpRequest, verifyOtpRequest } from '../authApi';
import { defaultPathForRoles, saveAuth } from '../authStorage';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import LanguageSelector from '../i18n/LanguageSelector';

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

const emptyRegister = () => ({
  branch: '',
  role: 'Secretary',
  username: '',
  email: '',
  phone: '',
  fullName: '',
  password: '',
  confirmPassword: '',
});

function LoginScreen({ onLogin }) {
  const { language, direction } = useLanguage();
  const t = useT();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [registerForm, setRegisterForm] = useState(emptyRegister);
  const [showPass, setShowPass] = useState(false);
  const [showRegPass, setShowRegPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpForm, setOtpForm] = useState({
    challengeId: '',
    maskedPhone: '',
    username: '',
    password: '',
    otpCode: '',
    displayOtpCode: '',
  });
  const [resendCounter, setResendCounter] = useState(0);
  const [error, setError] = useState('');
  const otpInputRefs = useRef([]);
  const otpDigits = useMemo(
    () => Array.from({ length: OTP_LENGTH }, (_, i) => otpForm.otpCode[i] ?? ''),
    [otpForm.otpCode]
  );

  useEffect(() => {
    if (!otpForm.challengeId || resendCounter <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendCounter((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [otpForm.challengeId, resendCounter]);

  const switchToRegister = () => {
    setError('');
    setRegisterForm(emptyRegister());
    setMode('register');
  };

  const switchToLogin = () => {
    setError('');
    setOtpForm({ challengeId: '', maskedPhone: '', username: '', password: '', otpCode: '', displayOtpCode: '' });
    setResendCounter(0);
    setMode('login');
  };

  const handleLoginChange = (e) => {
    setError('');
    setLoginForm({ ...loginForm, [e.target.name]: e.target.value });
  };

  const handleRegisterChange = (e) => {
    setError('');
    setRegisterForm({ ...registerForm, [e.target.name]: e.target.value });
  };

  const fillSampleData = () => {
    setError('');
    setRegisterForm({
      branch:          language === 'ar' || language === 'ur' ? 'شركة الأفق للاستثمار' : 'Horizon Investment Co.',
      role:            'Secretary',
      username:        'ceo_demo',
      email:           'ceo@horizon-demo.com',
      phone:           '0500000000',
      fullName:        language === 'ar' || language === 'ur' ? 'أحمد عبدالله الزهراني' : 'Ahmed Al-Zahrani',
      password:        'Demo@12345',
      confirmPassword: 'Demo@12345',
    });
  };

  const finalizeLogin = (data, userName, password) => {
    saveAuth(data);
    const path = defaultPathForRoles(data.user?.roles);
    if (onLogin) {
      onLogin({
        branch: data.user?.branchName,
        username: userName,
        password,
        roles: data.user?.roles,
        accessToken: data.accessToken,
      });
    }
    navigate(path);
  };

  const completeLogin = async (userName, password) => {
    const data = await loginRequest({ userName, password });

    // Block login if account is not yet approved by super admin
    if (data?.user?.isActive === false) {
      throw new Error(t('auth.accountPending'));
    }

    if (data?.requiresOtp) {
      const serverOtp = typeof data?.otpCode === 'string' ? data.otpCode.trim() : '';
      const prefill = serverOtp.length === OTP_LENGTH ? serverOtp : '';
      setOtpForm({
        challengeId: data.otpChallengeId ?? '',
        maskedPhone: data.otpMaskedPhone ?? '',
        username: userName,
        password,
        otpCode: prefill,
        displayOtpCode: serverOtp,
      });
      setResendCounter(RESEND_SECONDS);
      window.setTimeout(() => otpInputRefs.current[0]?.focus(), 10);
      return;
    }
    finalizeLogin(data, userName, password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!loginForm.username.trim() || !loginForm.password.trim()) {
      setError(t('auth.fillRequired'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await completeLogin(loginForm.username.trim(), loginForm.password);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    const c = registerForm.branch.trim();
    const u = registerForm.username.trim();
    const em = registerForm.email.trim();
    if (!c || !u || !em || !registerForm.password) {
      setError(t('auth.fillRequired'));
      return;
    }
    if (registerForm.password !== registerForm.confirmPassword) {
      setError(t('auth.passwordsNoMatch'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      await registerRequest({
        companyName: c,
        userName: u,
        email: em,
        phone: registerForm.phone.trim() || undefined,
        password: registerForm.password,
        role: registerForm.role,
        fullName: registerForm.fullName.trim() || undefined,
      });
      // Do NOT log in — show a pending-approval screen instead
      setMode('pending');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (!otpForm.challengeId || otpForm.otpCode.length !== OTP_LENGTH) {
      setError(t('auth.enterOtp'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await verifyOtpRequest({
        otpChallengeId: otpForm.challengeId,
        otpCode: otpForm.otpCode,
      });
      finalizeLogin(data, otpForm.username, otpForm.password);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  const updateOtpDigitAt = (index, value) => {
    const digits = otpDigits.slice();
    digits[index] = value;
    const joined = digits.join('').slice(0, OTP_LENGTH);
    setOtpForm({ ...otpForm, otpCode: joined });
  };

  const handleOtpDigitChange = (index, e) => {
    setError('');
    const raw = e.target.value ?? '';
    const numbers = raw.replace(/\D/g, '');
    if (!numbers) {
      updateOtpDigitAt(index, '');
      return;
    }
    if (numbers.length > 1) {
      const nextDigits = otpDigits.slice();
      let cursor = index;
      for (const ch of numbers) {
        if (cursor >= OTP_LENGTH) break;
        nextDigits[cursor] = ch;
        cursor += 1;
      }
      setOtpForm({ ...otpForm, otpCode: nextDigits.join('').slice(0, OTP_LENGTH) });
      otpInputRefs.current[Math.min(cursor, OTP_LENGTH - 1)]?.focus();
      return;
    }
    updateOtpDigitAt(index, numbers);
    if (index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpDigitKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    setError('');
    const digits = Array.from({ length: OTP_LENGTH }, (_, i) => pasted[i] ?? '');
    setOtpForm({ ...otpForm, otpCode: digits.join('') });
    otpInputRefs.current[Math.min(pasted.length, OTP_LENGTH) - 1]?.focus();
  };

  const handleResendOtp = async () => {
    if (!otpForm.challengeId || resendCounter > 0) return;
    setLoading(true);
    setError('');
    try {
      const data = await resendOtpRequest({ otpChallengeId: otpForm.challengeId });
      const serverOtp = typeof data?.otpCode === 'string' ? data.otpCode.trim() : '';
      const prefill = serverOtp.length === OTP_LENGTH ? serverOtp : '';
      setOtpForm({
        ...otpForm,
        challengeId: data.otpChallengeId ?? otpForm.challengeId,
        maskedPhone: data.otpMaskedPhone ?? otpForm.maskedPhone,
        otpCode: prefill,
        displayOtpCode: serverOtp,
      });
      setResendCounter(RESEND_SECONDS);
      window.setTimeout(() => otpInputRefs.current[0]?.focus(), 10);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  // Panel sub-title split on newline
  const panelSubLines = t('auth.panelSub').split('\n');

  return (
    <div className={`login ${direction === 'rtl' ? 'login--rtl' : 'login--ltr'}`}>

      {/* ── Welcome panel (60%) ───────────────────────────────────────── */}
      <div className="login__panel" style={{ backgroundImage: `linear-gradient(135deg, rgba(0,51,74,0.6), rgba(1,86,104,0.5)), url(${officeBg})` }}>
        <img src={logo} alt="Corp Pass" className="login__panel-logo" />

        <div className="login__panel-body">
          <h2 className="login__panel-title">{t('auth.welcome')}</h2>
          <p className="login__panel-sub">
            {panelSubLines[0]}{panelSubLines[1] ? <> {panelSubLines[1]}</> : null}
          </p>

          <div className="login__features">
            <div className="login__feature">
              <img src={iconMultiCompany} alt="" className="login__feature-icon" />
              <span className="login__feature-text">{t('auth.badge1')}</span>
            </div>
            <div className="login__feature">
              <img src={iconSecureVoting} alt="" className="login__feature-icon" />
              <span className="login__feature-text">{t('auth.badge2')}</span>
            </div>
            <div className="login__feature">
              <img src={iconOfficialDocs} alt="" className="login__feature-icon" />
              <span className="login__feature-text">{t('auth.badge3')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Login section (40%) ───────────────────────────────────────── */}
      <div className="login__side">
        <div className="login__lang">
          <LanguageSelector variant="icon" placement="bottom-start" />
        </div>
        <div className="login__card">

          <img src={logo} alt="Corp Pass" className="login__logo-sm" />

          <h1 className="login__heading">
            {mode === 'pending'
              ? t('auth.pendingTitle')
              : mode === 'login'
                ? (otpForm.challengeId ? t('auth.otpTitle') : t('auth.signIn'))
                : t('auth.createAccount')}
          </h1>
          <p className="login__desc">
            {mode === 'pending'
              ? ''
              : mode === 'login'
                ? (otpForm.challengeId
                  ? `${t('auth.otpDesc')} ${otpForm.maskedPhone || ''}`
                  : t('auth.enterAccount'))
                : t('auth.registerDesc')}
          </p>

          {mode === 'login' && !otpForm.challengeId && (
            <button type="button" className="login__ghost-btn" onClick={switchToRegister}>
              {t('auth.createAccount')}
            </button>
          )}

          {/* ── Pending approval screen ──────────────────────────────── */}
          {mode === 'pending' && (
            <div className="login__pending">
              <div className="login__pending-icon">⏳</div>
              <h2 className="login__pending-title">{t('auth.pendingTitle')}</h2>
              <p className="login__pending-desc">{t('auth.pendingDesc')}</p>
              <button type="button" className="login__btn" onClick={switchToLogin}>
                {t('auth.backToLoginBtn')}
              </button>
            </div>
          )}

          {/* ── OTP form ─────────────────────────────────────────────── */}
          {mode === 'login' ? (
            otpForm.challengeId ? (
              <form className="login__form" onSubmit={handleOtpSubmit} noValidate>
                {otpForm.displayOtpCode ? (
                  <div className="login__otp-dev" role="status">
                    <span className="login__otp-dev-label">{t('auth.otpDevBanner')}</span>
                    <span className="login__otp-dev-code" dir="ltr">{otpForm.displayOtpCode}</span>
                  </div>
                ) : null}

                <div className="login__field">
                  <label className="login__label">{t('auth.otpLabel')}</label>
                  <div className="login__otp-inputs" onPaste={handleOtpPaste}>
                    {otpDigits.map((digit, idx) => (
                      <input
                        key={`otp-${idx}`}
                        ref={(el) => { otpInputRefs.current[idx] = el; }}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        className={`login__otp-box${digit ? ' login__otp-box--filled' : ''}`}
                        value={digit}
                        onChange={(e) => handleOtpDigitChange(idx, e)}
                        onKeyDown={(e) => handleOtpDigitKeyDown(idx, e)}
                        autoComplete={idx === 0 ? 'one-time-code' : 'off'}
                        aria-label={`${t('auth.otpLabel')} ${idx + 1}`}
                      />
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="login__error">
                    <Icon name="warning" size={15} color="#c62828" />
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className={`login__btn${loading ? ' login__btn--loading' : ''}`}
                >
                  {loading
                    ? <>{t('auth.loadingVerify')} <span className="login__btn-spinner" /></>
                    : t('auth.verifyOtp')}
                </button>

                <button
                  type="button"
                  className="login__resend-btn"
                  disabled={loading || resendCounter > 0}
                  onClick={handleResendOtp}
                >
                  {resendCounter > 0
                    ? `${t('auth.resendIn')} ${resendCounter} ${t('auth.seconds')}`
                    : t('auth.resend')}
                </button>

                <button type="button" className="login__text-btn" onClick={switchToLogin}>
                  {t('auth.backToLogin')}
                </button>
              </form>

            ) : (
              /* ── Login form ─────────────────────────────────────── */
              <form className="login__form" onSubmit={handleSubmit} noValidate>

                <div className="login__field">
                  <label className="login__label" htmlFor="username">{t('auth.username')}</label>
                  <div className="login__input-wrap">
                    <input
                      id="username"
                      name="username"
                      type="text"
                      className="login__input"
                      placeholder={t('auth.enterUsername')}
                      value={loginForm.username}
                      onChange={handleLoginChange}
                      autoComplete="username"
                    />
                    <Icon name="user" size={20} color="#90a4ae" className="login__input-icon" />
                  </div>
                </div>

                <div className="login__field">
                  <label className="login__label" htmlFor="password">{t('auth.password')}</label>
                  <div className="login__input-wrap">
                    <input
                      id="password"
                      name="password"
                      type={showPass ? 'text' : 'password'}
                      className="login__input"
                      placeholder={t('auth.enterPassword')}
                      value={loginForm.password}
                      onChange={handleLoginChange}
                      autoComplete="current-password"
                    />
                    <Icon name="lock" size={20} color="#90a4ae" className="login__input-icon" />
                    <button
                      type="button"
                      className="login__pw-toggle"
                      onClick={() => setShowPass(!showPass)}
                      aria-label={showPass ? t('auth.hidePass') : t('auth.showPass')}
                    >
                      <Icon name={showPass ? 'eye-off' : 'eye'} size={14} color="#90a4ae" />
                    </button>
                  </div>
                </div>

                <div className="login__forgot">
                  <a href="#forgot" className="login__forgot-link">{t('auth.forgotPassword')}</a>
                </div>

                {error && (
                  <div className="login__error">
                    <Icon name="warning" size={15} color="#c62828" />
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className={`login__btn${loading ? ' login__btn--loading' : ''}`}
                >
                  {loading
                    ? <>{t('auth.loadingLogin')} <span className="login__btn-spinner" /></>
                    : t('auth.submitLogin')}
                </button>

                <div className="login__quick-login">
                  <span className="login__quick-login__label">
                    {t('auth.quickLogin')}
                  </span>
                  <div className="login__quick-login__btns">
                    {[
                      { labelKey: 'nav.superAdmin',  username: 'superadmin',       img: iconSuperAdmin },
                      { labelKey: 'auth.ceoRole',    username: 'demo_ceo',         img: iconCeo },
                      { labelKey: 'nav.secretary',   username: 'demo_secretary',   img: iconSecretary },
                      { labelKey: 'nav.boardMember', username: 'demo_board',       img: iconBoardMember },
                      { labelKey: 'nav.shareholders',username: 'demo_shareholder', img: iconShareholders },
                    ].map(({ labelKey, username, img }) => (
                      <button
                        key={username}
                        type="button"
                        className="login__quick-btn"
                        onClick={() => {
                          setError('');
                          setLoginForm({ username, password: 'Pass123' });
                        }}
                      >
                        <span className="login__quick-avatar">
                          <img src={img} alt="" />
                        </span>
                        <span className="login__quick-name">{t(labelKey)}</span>
                      </button>
                    ))}
                  </div>
                </div>

              </form>
            )

          ) : (
            /* ── Register form ───────────────────────────────────── */
            <form className="login__form" onSubmit={handleRegisterSubmit} noValidate>

              <div className="login__field">
                <label className="login__label" htmlFor="reg-branch">{t('auth.regBranch')}</label>
                <div className="login__input-wrap">
                  <input
                    id="reg-branch"
                    name="branch"
                    type="text"
                    className="login__input"
                    placeholder={t('auth.regBranchPh')}
                    value={registerForm.branch}
                    onChange={handleRegisterChange}
                    autoComplete="organization"
                  />
                  <Icon name="branch" size={20} color="#90a4ae" className="login__input-icon" />
                </div>
              </div>

              <div className="login__field">
                <label className="login__label" htmlFor="reg-username">{t('auth.username')}</label>
                <div className="login__input-wrap">
                  <input
                    id="reg-username"
                    name="username"
                    type="text"
                    className="login__input"
                    placeholder={t('auth.enterUsername')}
                    value={registerForm.username}
                    onChange={handleRegisterChange}
                    autoComplete="username"
                  />
                  <Icon name="user" size={20} color="#90a4ae" className="login__input-icon" />
                </div>
              </div>

              <div className="login__field">
                <label className="login__label" htmlFor="reg-email">{t('common.email')}</label>
                <div className="login__input-wrap">
                  <input
                    id="reg-email"
                    name="email"
                    type="email"
                    className="login__input"
                    placeholder="you@company.com"
                    value={registerForm.email}
                    onChange={handleRegisterChange}
                    autoComplete="email"
                  />
                  <Icon name="user" size={20} color="#90a4ae" className="login__input-icon" />
                </div>
              </div>

              <div className="login__field">
                <label className="login__label" htmlFor="reg-phone">{t('common.phone')}</label>
                <div className="login__input-wrap">
                  <input
                    id="reg-phone"
                    name="phone"
                    type="tel"
                    className="login__input"
                    placeholder="05xxxxxxxx"
                    value={registerForm.phone}
                    onChange={handleRegisterChange}
                    autoComplete="tel"
                    dir="ltr"
                  />
                  <Icon name="chat" size={20} color="#90a4ae" className="login__input-icon" />
                </div>
              </div>

              <div className="login__field">
                <label className="login__label" htmlFor="reg-fullname">{t('auth.fullNameOptional')}</label>
                <div className="login__input-wrap">
                  <input
                    id="reg-fullname"
                    name="fullName"
                    type="text"
                    className="login__input"
                    placeholder={t('auth.fullNamePh')}
                    value={registerForm.fullName}
                    onChange={handleRegisterChange}
                    autoComplete="name"
                  />
                  <Icon name="user" size={20} color="#90a4ae" className="login__input-icon" />
                </div>
              </div>

              <div className="login__field">
                <label className="login__label" htmlFor="reg-password">{t('auth.password')}</label>
                <div className="login__input-wrap">
                  <input
                    id="reg-password"
                    name="password"
                    type={showRegPass ? 'text' : 'password'}
                    className="login__input"
                    placeholder={t('auth.passRule')}
                    value={registerForm.password}
                    onChange={handleRegisterChange}
                    autoComplete="new-password"
                  />
                  <Icon name="lock" size={20} color="#90a4ae" className="login__input-icon" />
                  <button
                    type="button"
                    className="login__pw-toggle"
                    onClick={() => setShowRegPass(!showRegPass)}
                    aria-label={showRegPass ? t('auth.hidePass') : t('auth.showPass')}
                  >
                    <Icon name={showRegPass ? 'eye-off' : 'eye'} size={14} color="#90a4ae" />
                  </button>
                </div>
              </div>

              <div className="login__field">
                <label className="login__label" htmlFor="reg-confirm">{t('auth.confirmPassword')}</label>
                <div className="login__input-wrap">
                  <input
                    id="reg-confirm"
                    name="confirmPassword"
                    type={showRegPass ? 'text' : 'password'}
                    className="login__input"
                    placeholder={t('auth.confirmPasswordPh')}
                    value={registerForm.confirmPassword}
                    onChange={handleRegisterChange}
                    autoComplete="new-password"
                  />
                  <Icon name="lock" size={20} color="#90a4ae" className="login__input-icon" />
                </div>
              </div>

              {error && (
                <div className="login__error">
                  <Icon name="warning" size={15} color="#c62828" />
                  {error}
                </div>
              )}

              <button
                type="button"
                className="login__fill-btn"
                onClick={fillSampleData}
                tabIndex={-1}
              >
                {t('auth.fillSample')}
              </button>

              <button
                type="submit"
                className={`login__btn${loading ? ' login__btn--loading' : ''}`}
              >
                {loading
                  ? <>{t('auth.loadingRegister')} <span className="login__btn-spinner" /></>
                  : t('auth.createAccount')}
              </button>

              <button type="button" className="login__text-btn" onClick={switchToLogin}>
                {t('auth.haveAccount')}
              </button>

            </form>
          )}

        </div>
      </div>

      <p className="login__footer">
        {t('auth.rights')} © Corp Pass {new Date().getFullYear()}
      </p>

    </div>
  );
}

export default LoginScreen;
